import { Component, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { KeapLicenseMappingProvider } from './components/KeapLicenseMappingProvider';
import { OptimizedCampaignDataProvider } from '@/contexts/OptimizedCampaignDataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import PostInvite from './pages/PostInvite';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';
import ResetPortalPassword from './pages/portal/ResetPassword.tsx';
import ForgotPortalPassword from './pages/portal/ForgotPassword.tsx';
import Dashboard from './pages/Dashboard';
import ApiSettings from './pages/ApiSettings';
import ScriptManagement from './pages/ScriptManagement';
import ProspectManagement from './pages/ProspectManagement';
import Campaigns from './pages/Campaigns';
import Training from './pages/Training';
import Help from './pages/Help';
import AdminDashboard from './pages/AdminDashboard';
import Actions from './pages/Actions';
import QATestHarness from './pages/QATestHarness';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import CreditPackages from './pages/CreditPackages';
import Clients from './pages/Clients';
import BusinessPortal from './pages/BusinessPortal';
import CampaignsManagement from './components/admin/CampaignsManagement';
import PortalAuth from './pages/portal/PortalAuth';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalActions from './pages/portal/PortalActions';
import { TenantProvider, useTenant } from './hooks/useTenant.tsx';
import { PublicRoute } from './components/PublicRoute';

// console.log('App mounted with hostname:', window.location.hostname);

const queryClient = new QueryClient();

// Root component to handle / redirect
const Root = () => {
  let auth;
  try {
    auth = useAuth();
  } catch (err) {
    console.error(`[Root] [${new Date().toISOString()}] useAuth error:`, err);
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-500 text-sm'>
            Authentication error. Please try again or log in.
          </p>
          <button
            className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
            onClick={() => window.location.assign('/login')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  const { user, loading: authLoading } = auth;
  const { tenant, isCustomDomain, loading: tenantLoading } = useTenant();
  const location = useLocation();

  // useEffect(() => {
  //   console.log(`[Root] [${new Date().toISOString()}] Component mounted`, {
  //     user: user?.id,
  //     authLoading,
  //     isCustomDomain,
  //   });
  // }, [user, authLoading, isCustomDomain]);

  if (authLoading || tenantLoading) {
    // Updated to include tenantLoading
    // console.log(
    //   `[Root] [${new Date().toISOString()}] Auth or tenant loading, showing spinner`
    // );
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary'></div>
          <p className='mt-4 text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  // console.log(
  //   `[Root] After loading: isCustomDomain=${isCustomDomain}, tenantId=${
  //     tenant?.id || 'null'
  //   }, hostname=${window.location.hostname}`
  // );

  // Updated redirect logic
  const redirectTo = isCustomDomain
    ? user
      ? '/portal/dashboard'
      : '/portal/auth'
    : user
      ? '/dashboard'
      : location.pathname.startsWith('/portal')
        ? '/portal/auth'
        : '/login';

  // console.log(`[Root] [${new Date().toISOString()}] Redirecting:`, {
  //   user: user?.id,
  //   to: redirectTo,
  //   isCustomDomain,
  //   tenantId: tenant?.id || 'null',
  // });

  return <Navigate to={redirectTo} replace />;
};

const AppContentInner = () => {
  let auth;
  try {
    auth = useAuth();
  } catch (err) {
    console.error(
      `[AppContent] [${new Date().toISOString()}] useAuth error:`,
      err,
    );
    throw err; // Let error boundary catch
  }
  const { loading } = auth;

  // useEffect(() => {
  //   console.log(
  //     `[AppContent] [${new Date().toISOString()}] Auth loading state:`,
  //     loading
  //   );
  // }, [loading]);

  return (
    <KeapLicenseMappingProvider>
      <OptimizedCampaignDataProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path='/login'
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route path='/portal/auth' element={<PortalAuth />} />
            <Route path='/portal/invite' element={<PortalAuth />} />
            <Route
              path='/portal/reset-password'
              element={<ResetPortalPassword />}
            />
            <Route
              path='/portal/forgot-password'
              element={<ForgotPortalPassword />}
            />
            <Route
              path='/post-invite'
              element={
                <PublicRoute>
                  <PostInvite />
                </PublicRoute>
              }
            />
            <Route
              path='/forgot-password'
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path='/reset-password'
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path='/portal/dashboard'
              element={
                <ProtectedRoute>
                  <PortalDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path='/portal/client/:clientId/actions'
              element={
                <ProtectedRoute>
                  <PortalActions />
                </ProtectedRoute>
              }
            />
            <Route path='/portal/*' element={<Root />} /> // Fallback to Root
            for any /portal/...
            <Route path='/' element={<Root />} />
            <Route
              path='/set-password'
              element={
                <ProtectedRoute>
                  <SetPassword />
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin'
              element={<Navigate to='/admin/users' replace />}
            />
            <Route
              path='/admin/users'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/licenses'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/tag-mapping'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/billing'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/api-logs'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/campaigns'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/training-editor'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/help-center-editor'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/settings'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/templates'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/script-templates'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/action-types'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/message-center'
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/dashboard'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/api-settings'
              element={
                <ProtectedRoute>
                  <Layout>
                    <ApiSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/script-management'
              element={
                <ProtectedRoute>
                  <Layout>
                    <ScriptManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/script-editor'
              element={
                <ProtectedRoute>
                  <Layout>
                    <ScriptManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/campaigns'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Campaigns />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/prospects'
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProspectManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/actions'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Actions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/clients'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Clients />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/business-portal'
              element={
                <ProtectedRoute>
                  <Layout>
                    <BusinessPortal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/campaign-assignment'
              element={
                <ProtectedRoute>
                  <Layout>
                    <CampaignsManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/qa-test'
              element={
                <ProtectedRoute adminOnly>
                  <Layout>
                    <QATestHarness />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/analytics'
              element={
                <ProtectedRoute>
                  <Layout>
                    <div className='p-6'>Analytics - Coming Soon</div>
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/training'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Training />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/help'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Help />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/profile'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/credit-packages'
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreditPackages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Commented out for testing, may be redundant */}
            {/* <Route
              path='/portal/*'
              element={<Navigate to='/portal/dashboard' replace />}
            /> */}
            <Route
              path='*'
              element={
                <ProtectedRoute>
                  <NotFound />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </OptimizedCampaignDataProvider>
    </KeapLicenseMappingProvider>
  );
};

// Error boundary wrapper for AppContent
class AppContent extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error(
      `[AppContentErrorBoundary] [${new Date().toISOString()}] Caught error:`,
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      const errorInfo = this.state.error;
      const message = errorInfo?.message || String(errorInfo);
      const stack = errorInfo?.stack;

      return (
        <div className='min-h-screen flex items-center justify-center'>
          <div className='text-center max-w-2xl'>
            <p className='text-red-500 text-sm mb-2'>
              An error occurred while loading the application. Please try again
              or refresh the page.
            </p>
            <p className='text-sm font-medium'>Error:</p>
            <pre className='text-xs text-left bg-gray-900/50 p-3 rounded mb-4 break-words'>
              {message}
              {stack ? `\n\n${stack}` : null}
            </pre>
            <button
              className='mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
              onClick={() => window.location.assign('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      );
    }

    return <AppContentInner />;
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute='class'
      defaultTheme='system'
      enableSystem
      disableTransitionOnChange={false}
    >
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <TenantProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </TenantProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
